import React from 'react';

// Dashboard View Skeleton
export const DashboardViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
              <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
            <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="w-full h-80 bg-gray-300 rounded animate-pulse"></div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="w-full h-80 bg-gray-300 rounded animate-pulse"></div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
              <div className="flex-1">
                <div className="w-3/4 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                <div className="w-1/2 h-3 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-16 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Patient Management View Skeleton
export const PatientViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header with Search and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-64 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>

      {/* Patient Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="w-32 h-5 bg-gray-300 rounded animate-pulse"></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {Array.from({ length: 6 }).map((_, index) => (
                  <th key={index} className="px-6 py-3 text-left">
                    <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Array.from({ length: 8 }).map((_, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
                      <div>
                        <div className="w-24 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                        <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </td>
                  {Array.from({ length: 5 }).map((_, colIndex) => (
                    <td key={colIndex} className="px-6 py-4">
                      <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Doctor Management View Skeleton
export const DoctorViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Doctor Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-gray-300 rounded-full animate-pulse"></div>
              <div className="flex-1">
                <div className="w-32 h-5 bg-gray-300 rounded animate-pulse mb-2"></div>
                <div className="w-24 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                <div className="w-28 h-3 bg-gray-300 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="flex justify-between">
                <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
                <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-full h-2 bg-gray-300 rounded-full animate-pulse"></div>
            </div>
            <div className="flex space-x-2 mt-4">
              <div className="w-16 h-8 bg-gray-300 rounded animate-pulse"></div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Appointment View Skeleton
export const AppointmentViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Calendar View */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="w-32 h-6 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-2">
            <div className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="w-full h-8 bg-gray-300 rounded animate-pulse"></div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }).map((_, index) => (
            <div key={index} className="aspect-square bg-gray-100 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>

      {/* Today's Appointments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gray-300 rounded-full animate-pulse"></div>
              <div className="flex-1">
                <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                <div className="w-48 h-3 bg-gray-300 rounded animate-pulse mb-1"></div>
                <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Billing View Skeleton
export const BillingViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
              <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
            </div>
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
            <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
          </div>
        ))}
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="w-32 h-5 bg-gray-300 rounded animate-pulse"></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {Array.from({ length: 7 }).map((_, index) => (
                  <th key={index} className="px-6 py-3 text-left">
                    <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Array.from({ length: 6 }).map((_, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {Array.from({ length: 7 }).map((_, colIndex) => (
                    <td key={colIndex} className="px-6 py-4">
                      <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Reports View Skeleton
export const ReportsViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Report Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="w-32 h-5 bg-gray-300 rounded animate-pulse mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="w-full h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="w-full h-80 bg-gray-300 rounded animate-pulse"></div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="w-full h-80 bg-gray-300 rounded animate-pulse"></div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="w-32 h-5 bg-gray-300 rounded animate-pulse mb-6"></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {Array.from({ length: 5 }).map((_, index) => (
                  <th key={index} className="px-6 py-3 text-left">
                    <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Array.from({ length: 8 }).map((_, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {Array.from({ length: 5 }).map((_, colIndex) => (
                    <td key={colIndex} className="px-6 py-4">
                      <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Settings View Skeleton
export const SettingsViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
      </div>

      {/* Settings Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="py-4">
                <div className="w-24 h-5 bg-gray-300 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Settings Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
                  <div className="w-full h-10 bg-gray-300 rounded-lg animate-pulse"></div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <div className="w-40 h-4 bg-gray-300 rounded animate-pulse"></div>
                  <div className="w-full h-20 bg-gray-300 rounded-lg animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Form View Skeleton
export const FormViewSkeleton: React.FC<{ fields?: number; columns?: number }> = ({ 
  fields = 8, 
  columns = 2 
}) => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-20 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className={`grid grid-cols-1 ${columns === 2 ? 'lg:grid-cols-2' : ''} gap-6`}>
          {Array.from({ length: fields }).map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
              <div className="w-full h-10 bg-gray-300 rounded-lg animate-pulse"></div>
            </div>
          ))}
        </div>
        
        {/* Form Actions */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
          <div className="w-20 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

// Detail View Skeleton
export const DetailViewSkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
        <div className="flex space-x-3">
          <div className="w-20 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      </div>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Main Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-20 h-20 bg-gray-300 rounded-full animate-pulse"></div>
              <div className="flex-1">
                <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-2"></div>
                <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                <div className="w-40 h-3 bg-gray-300 rounded animate-pulse"></div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                  <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-32 h-5 bg-gray-300 rounded animate-pulse mb-4"></div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex justify-between">
                  <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
                  <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="w-32 h-5 bg-gray-300 rounded animate-pulse mb-4"></div>
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, itemIndex) => (
                  <div key={itemIndex} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
                    <div className="flex-1">
                      <div className="w-24 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                      <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
